import {
  TrackingRequestError,
  derivePlaybackSegments,
  playbackAlertsOf,
  isPositioned,
  type GeoBounds,
  type TrackedVehicle,
  type TrackingConnectionState,
  type TrackingListState,
  type TrackingListWatch,
  type TrackingRepository,
  type TrackingScope,
  type TrackingSnapshot,
  type TrackingStreamEvent,
  type TrackingVehicleCounts,
  type TrackingVehicleQuery,
  type VehicleListStatus,
  type VehicleTelemetryEvent,
  type VehicleTrackingStream,
} from "../domain";
import { createPlaybackPoints, trackingAccounts, trackingVehicles } from "../testing/fixtures";

/** The Asset List's status for a vehicle, as the backend counts it. */
function listStatusOf(vehicle: TrackedVehicle): VehicleListStatus {
  if (vehicle.telemetry !== null) return vehicle.telemetry.status;
  return vehicle.hasDevice ? "offline" : "no_device";
}

/** The backend's list rules on fixtures: company, search (name in either
 *  language, code, IMEI), status, sorted by name. */
function listed(query: Omit<TrackingVehicleQuery, "page" | "limit">): TrackedVehicle[] {
  const search = query.search?.trim().toLocaleLowerCase() ?? "";
  return trackingVehicles
    .filter(
      (vehicle) =>
        (query.accountId === undefined || vehicle.account?.id === query.accountId) &&
        (search === "" ||
          [vehicle.name, vehicle.nameAr, vehicle.code, vehicle.imei ?? ""]
            .join(" ")
            .toLocaleLowerCase()
            .includes(search)) &&
        (query.status === undefined || listStatusOf(vehicle) === query.status),
    )
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** The counters for a company and search, as the backend counts them. */
function countsOf(query: Omit<TrackingVehicleQuery, "status" | "page" | "limit">): TrackingVehicleCounts {
  const statuses = listed(query).map(listStatusOf);
  const count = (status: VehicleListStatus) => statuses.filter((item) => item === status).length;
  return {
    total: statuses.length,
    online: count("moving") + count("idle") + count("stopped"),
    moving: count("moving"),
    idle: count("idle"),
    stopped: count("stopped"),
    offline: count("offline"),
    noDevice: count("no_device"),
  };
}

export function createMockTrackingRepository(): TrackingRepository {
  return {
    listAccounts() {
      return Promise.resolve(trackingAccounts);
    },
    listVehicles(query) {
      const all = listed(query);
      const totalPages = Math.ceil(all.length / query.limit);
      return Promise.resolve({
        data: all.slice((query.page - 1) * query.limit, query.page * query.limit),
        meta: {
          totalCount: all.length,
          currentPage: query.page,
          totalPages,
          firstPageUrl: "/tracking/vehicles?page=1",
          lastPageUrl: `/tracking/vehicles?page=${String(Math.max(totalPages, 1))}`,
          nextPageUrl: query.page < totalPages ? `/tracking/vehicles?page=${String(query.page + 1)}` : null,
          previousPageUrl: query.page > 1 ? `/tracking/vehicles?page=${String(query.page - 1)}` : null,
        },
      });
    },
    countVehicles(query) {
      return Promise.resolve(countsOf(query));
    },
    listVehicleIds(query) {
      return Promise.resolve(listed(query).map((vehicle) => vehicle.id));
    },
    getPlayback(vehicleId, range) {
      const vehicle = trackingVehicles.find((item) => item.id === vehicleId);
      // Points only, like the endpoint; segments the same way as for real.
      const points =
        vehicle === undefined || !isPositioned(vehicle) ? [] : createPlaybackPoints(vehicle, range);
      return Promise.resolve({
        points,
        segments: derivePlaybackSegments(vehicleId, points),
        alerts: playbackAlertsOf(points),
      });
    },
    getAddress() {
      // An address in every language, so the mock shows the filled state.
      return Promise.resolve({ en: "Olaya St, Al Olaya, Riyadh", ar: "شارع العليا، العليا، الرياض" });
    },
  };
}

function inBounds(vehicle: TrackedVehicle, bounds: GeoBounds): boolean {
  if (vehicle.telemetry === null) return false;
  const { latitude, longitude } = vehicle.telemetry;
  const withinLongitude =
    bounds.east >= bounds.west
      ? longitude >= bounds.west && longitude <= bounds.east
      : longitude >= bounds.west || longitude <= bounds.east;
  return latitude >= bounds.south && latitude <= bounds.north && withinLongitude;
}

/** The feed's rules for what a scope holds: the company's vehicles, inside
 *  the bounds when given, plus the followed ones wherever they are. */
function vehiclesInScope(scope: TrackingScope): TrackedVehicle[] {
  const followed = new Set(scope.assetIds ?? []);
  return trackingVehicles.filter(
    (vehicle) =>
      (scope.accountId === undefined || vehicle.account?.id === scope.accountId) &&
      (scope.bounds === undefined || inBounds(vehicle, scope.bounds) || followed.has(vehicle.id)),
  );
}

export interface MockVehicleTrackingStream extends VehicleTrackingStream {
  /** Demo/test hook — the real port has no such affordance, but nothing else
   * here drives a "reconnecting" transition otherwise. */
  simulateReconnect(): void;
}

/** The live feed on fixtures: the same contract as the socket, with moving
 *  vehicles wandering around their start. */
export function createMockVehicleTrackingStream(
  intervalMs = 1500,
): MockVehicleTrackingStream {
  const listeners = new Set<(event: TrackingStreamEvent) => void>();
  const connectionListeners = new Set<(state: TrackingConnectionState) => void>();
  const sequences = new Map(trackingVehicles.map((vehicle) => [vehicle.id, 1]));
  let scope: TrackingScope | null = null;
  let followedIds = new Set<string>();
  let listWatch: TrackingListWatch | null = null;
  // The last telemetry sent per vehicle, for a watch's answer.
  const latest = new Map(
    trackingVehicles.flatMap((vehicle) =>
      vehicle.telemetry === null ? [] : [[vehicle.id, vehicle.telemetry] as const],
    ),
  );
  let timer: ReturnType<typeof setInterval> | undefined;
  let tick = 0;

  const publish = (event: TrackingStreamEvent) => {
    listeners.forEach((listener) => listener(event));
  };
  const emitConnection = (state: TrackingConnectionState) => {
    connectionListeners.forEach((listener) => listener(state));
  };

  const snapshotOf = (forScope: TrackingScope): TrackingSnapshot => {
    const vehicles = vehiclesInScope(forScope);
    followedIds = new Set(vehicles.map((vehicle) => vehicle.id));
    return { vehicles, capturedAt: new Date().toISOString() };
  };

  const listStateOf = (watch: TrackingListWatch): TrackingListState => ({
    events: watch.assetIds.flatMap((vehicleId) => {
      const telemetry = latest.get(vehicleId);
      return telemetry === undefined ? [] : [{ vehicleId, telemetry }];
    }),
    counts: countsOf({
      ...(scope?.accountId === undefined ? {} : { accountId: scope.accountId }),
      ...(watch.search === undefined ? {} : { search: watch.search }),
    }),
  });

  const step = () => {
    tick += 1;
    const events: VehicleTelemetryEvent[] = [];
    const watchedIds = new Set(listWatch?.assetIds ?? []);
    for (const [index, vehicle] of trackingVehicles.entries()) {
      const telemetry = vehicle.telemetry;
      if (
        !(followedIds.has(vehicle.id) || watchedIds.has(vehicle.id)) ||
        telemetry === null ||
        telemetry.status === "offline"
      ) {
        continue;
      }
      const moving = telemetry.status === "moving";
      const sequence = (sequences.get(vehicle.id) ?? 1) + 1;
      sequences.set(vehicle.id, sequence);
      const phase = tick * 0.23 + index;
      const now = new Date().toISOString();
      events.push({
        vehicleId: vehicle.id,
        telemetry: {
          ...telemetry,
          sequence,
          recordedAt: now,
          gpsTime: now,
          lastCommunicationAt: now,
          latitude: telemetry.latitude + (moving ? Math.sin(phase) * 0.003 : 0),
          longitude: telemetry.longitude + (moving ? Math.cos(phase) * 0.003 : 0),
          heading:
            moving && telemetry.heading !== null
              ? (telemetry.heading + tick * 9) % 360
              : telemetry.heading,
          speedKph: moving ? Math.max(12, telemetry.speedKph + Math.sin(phase) * 8) : 0,
        },
      });
    }
    for (const event of events) latest.set(event.vehicleId, event.telemetry);
    // The map's feed and the list's carry the same telemetry, each for its own.
    const mapEvents = events.filter((event) => followedIds.has(event.vehicleId));
    const listEvents = events.filter((event) => watchedIds.has(event.vehicleId));
    if (mapEvents.length > 0) publish({ type: "telemetry", events: mapEvents });
    if (listEvents.length > 0) publish({ type: "list-telemetry", events: listEvents });
  };

  const ensureRunning = () => {
    if (timer !== undefined) return;
    emitConnection("connecting");
    queueMicrotask(() => emitConnection("connected"));
    timer = setInterval(step, intervalMs);
  };

  return {
    connect: ensureRunning,
    disconnect() {
      if (timer !== undefined) clearInterval(timer);
      timer = undefined;
      scope = null;
      followedIds = new Set();
      listWatch = null;
      emitConnection("disconnected");
    },
    setScope(next) {
      ensureRunning();
      scope = next;
      listWatch = null;
      const snapshot = snapshotOf(next);
      publish({ type: "snapshot", snapshot });
      return Promise.resolve(snapshot);
    },
    setViewport(viewport) {
      if (scope === null) {
        return Promise.reject(
          new TrackingRequestError({ code: "TRACKING_NOT_SUBSCRIBED", message: "" }),
        );
      }
      const accountId = scope.accountId;
      scope = { ...viewport, ...(accountId === undefined ? {} : { accountId }) };
      const snapshot = snapshotOf(scope);
      publish({ type: "visible", vehicles: snapshot.vehicles });
      return Promise.resolve(snapshot);
    },
    watchList(watch) {
      if (scope === null) {
        return Promise.reject(
          new TrackingRequestError({ code: "TRACKING_NOT_SUBSCRIBED", message: "" }),
        );
      }
      listWatch = watch;
      const state = listStateOf(watch);
      publish({ type: "list-watched", state });
      return Promise.resolve(state);
    },
    unwatchList() {
      listWatch = null;
    },
    clearScope() {
      scope = null;
      followedIds = new Set();
      listWatch = null;
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    subscribeToConnection(listener) {
      connectionListeners.add(listener);
      return () => connectionListeners.delete(listener);
    },
    simulateReconnect() {
      if (timer === undefined) return;
      emitConnection("reconnecting");
      setTimeout(() => {
        emitConnection("connected");
        if (scope !== null) publish({ type: "snapshot", snapshot: snapshotOf(scope) });
        if (listWatch !== null) publish({ type: "list-watched", state: listStateOf(listWatch) });
      }, 800);
    },
  };
}
